import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Flex,
  Heading,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';
import { useFitGridColumns } from '../fitGridColumns';
import { withFleets } from '../withFleets';

const TABS = [
  { id: 'attribution', label: 'Needs attribution' },
  { id: 'collection', label: 'Collection' },
];

const COLLECTION_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'paid', label: 'Paid' },
  { id: 'waived', label: 'Waived' },
];

function fleetsQuery(selectedFleetCode) {
  const query = {};
  if (selectedFleetCode) query.fleetCode = selectedFleetCode;
  return query;
}

const ATTR_PAGE_SIZE = 200;

function formatUnixUtc(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n * 1000).toISOString().replace('T', ' ').replace('.000Z', ' UTC');
}

function suggestionLabel(s) {
  const start = formatUnixUtc(s.period_start);
  const end = formatUnixUtc(s.period_end);
  const window = start && end ? ` — car on ${start} to ${end}` : '';
  return `${s.driver_name} (driver ${s.driver_id}) — ${s.res_id} [${s.status}]${window}`;
}

function TabBar({ active, onChange, badges }) {
  return (
    <Flex gap="0.5rem" marginTop="1rem" marginBottom="0.5rem" wrap="wrap">
      {TABS.map((t) => {
        const badge = badges?.[t.id];
        const selected = active === t.id;
        return (
          <Button
            key={t.id}
            size="small"
            variation={selected ? 'primary' : undefined}
            onClick={() => onChange(t.id)}
          >
            {t.label}
            {badge != null ? ` (${badge})` : ''}
          </Button>
        );
      })}
    </Flex>
  );
}

function AttributionPanel({
  selectedFleetCode,
  chargingEnabled,
  rows,
  totalPending,
  pageOffset,
  pageSize,
  onPage,
  isLoading,
  error,
  onRefresh,
}) {
  const [assignRow, setAssignRow] = useState(null);
  const [dismissRow, setDismissRow] = useState(null);
  const [driverId, setDriverId] = useState('');
  const [resId, setResId] = useState('');
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const columnsFit = useFitGridColumns(false);

  async function postAction(body) {
    setActionLoading(true);
    setActionError('');
    try {
      await api3Post('/fleets/tolls-queue', body, fleetsQuery(selectedFleetCode));
      setAssignRow(null);
      setDismissRow(null);
      setDriverId('');
      setResId('');
      setReason('');
      onRefresh();
    } catch (e) {
      setActionError(e.message || 'Action failed');
    }
    setActionLoading(false);
  }

  function openAssign(row) {
    setDismissRow(null);
    setAssignRow(row);
    const sug = (row.suggested_reservations || [])[0];
    setDriverId(sug?.driver_id ? String(sug.driver_id) : '');
    setResId(sug?.res_id || '');
    setReason('');
  }

  const columns = useMemo(
    () => [
      { headerName: 'Fleet', field: 'fleet_code', width: 100, filter: true },
      { headerName: 'When (UTC ts)', field: 'utc_transaction_timestamp', width: 140 },
      { headerName: 'Local time', field: 'transaction_local_datetime', width: 170 },
      { headerName: 'Ingested', field: 'created', width: 170 },
      { headerName: 'Amount', field: 'amount', width: 100 },
      { headerName: 'Car', field: 'car_name', width: 140, filter: true },
      { headerName: 'Plate', field: 'lic_plate', width: 110, filter: true },
      { headerName: 'Location', field: 'location', minWidth: 160, filter: true },
      {
        headerName: 'Suggestions',
        field: 'suggestion_count',
        width: 120,
        cellRenderer: (p) => {
          const n = p.value || 0;
          if (n === 1) return '1 match';
          if (n > 1) return `${n} ambiguous`;
          return 'none';
        },
      },
      {
        headerName: 'Actions',
        width: 200,
        cellRenderer: (params) => (
          <Flex gap="0.5rem">
            <Button size="small" onClick={() => openAssign(params.data)}>
              Assign
            </Button>
            <Button
              size="small"
              variation="warning"
              onClick={() => {
                setAssignRow(null);
                setDismissRow(params.data);
                setReason('');
              }}
            >
              Dismiss
            </Button>
          </Flex>
        ),
      },
    ],
    []
  );

  return (
    <View>
      <Text>
        Unmatched plate-matched tolls ({totalPending}). Assign to a parent reservation/driver or
        dismiss with a reason.
      </Text>
      {totalPending > 0 ? (
        <Flex gap="0.75rem" alignItems="center" marginTop="0.5rem" wrap="wrap">
          <Text>
            Showing {pageOffset + 1}–{Math.min(pageOffset + rows.length, totalPending)} of{' '}
            {totalPending}
          </Text>
          <Button
            size="small"
            isDisabled={pageOffset <= 0 || isLoading}
            onClick={() => onPage(Math.max(0, pageOffset - pageSize))}
          >
            Previous
          </Button>
          <Button
            size="small"
            isDisabled={pageOffset + pageSize >= totalPending || isLoading}
            onClick={() => onPage(pageOffset + pageSize)}
          >
            Next
          </Button>
        </Flex>
      ) : null}
      {!chargingEnabled ? (
        <Text marginTop="0.5rem">
          <i>
            Auto-charging is currently disabled (INCIDENTALS_CHARGING_ENABLED) — tolls can still be
            assigned but will not be collected until charging is enabled.
          </i>
        </Text>
      ) : null}
      {error || actionError ? (
        <Alert variation="error" marginTop="1rem">
          {error || actionError}
        </Alert>
      ) : null}
      {isLoading || actionLoading ? <Loadingmini /> : null}
      <div
        ref={columnsFit.rootRef}
        className="ag-theme-quartz"
        style={{ height: 520, width: '100%', marginTop: '1rem' }}
      >
        <AgGridReact
          rowData={rows}
          columnDefs={columns}
          theme={themeQuartz}
          onGridReady={columnsFit.onGridReady}
          onFirstDataRendered={columnsFit.onFirstDataRendered}
          onRowDataUpdated={columnsFit.onRowDataUpdated}
        />
      </div>

      {assignRow ? (
        <View
          marginTop="1.5rem"
          padding="1rem"
          backgroundColor="var(--amplify-colors-background-secondary)"
        >
          <Heading level={5}>Assign toll {assignRow.toll_id}</Heading>
          {(assignRow.suggested_reservations || []).length > 0 ? (
            <View marginBottom="0.75rem">
              <Text fontWeight="bold">Suggested reservations</Text>
              {(assignRow.suggested_reservations || []).map((s) => (
                <Flex key={s.res_id} gap="0.5rem" alignItems="center" marginTop="0.25rem">
                  <Text>
                    {suggestionLabel(s)}
                  </Text>
                  <Button
                    size="small"
                    onClick={() => {
                      setDriverId(String(s.driver_id));
                      setResId(s.res_id || '');
                    }}
                  >
                    Use
                  </Button>
                  <NavLink to={withFleets(`/user/${s.driver_id}`, assignRow.fleet_code || selectedFleetCode)}>Open driver</NavLink>
                </Flex>
              ))}
            </View>
          ) : null}
          <TextField
            label="Driver ID"
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            marginBottom="0.5rem"
          />
          <TextField
            label="Reservation ID (optional)"
            value={resId}
            onChange={(e) => setResId(e.target.value)}
            marginBottom="0.5rem"
          />
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            marginBottom="0.75rem"
          />
          <Flex gap="0.5rem">
            <Button
              variation="primary"
              onClick={() =>
                postAction({
                  action: 'assign',
                  tollId: assignRow.toll_id,
                  driverId: Number(driverId),
                  resId: resId || null,
                  reason: reason || null,
                })
              }
            >
              Confirm assign
            </Button>
            <Button onClick={() => setAssignRow(null)}>Cancel</Button>
          </Flex>
        </View>
      ) : null}

      {dismissRow ? (
        <View
          marginTop="1.5rem"
          padding="1rem"
          backgroundColor="var(--amplify-colors-background-secondary)"
        >
          <Heading level={5}>Dismiss toll {dismissRow.toll_id}</Heading>
          <TextField
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            marginBottom="0.75rem"
          />
          <Flex gap="0.5rem">
            <Button
              variation="warning"
              onClick={() =>
                postAction({
                  action: 'dismiss',
                  tollId: dismissRow.toll_id,
                  reason,
                })
              }
            >
              Confirm dismiss
            </Button>
            <Button onClick={() => setDismissRow(null)}>Cancel</Button>
          </Flex>
        </View>
      ) : null}
    </View>
  );
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function CollectionPanel({
  selectedFleetCode,
  chargingEnabled,
  rows,
  statusCounts,
  totalOwed,
  totalCollected,
  unpaidCount,
  statusFilter,
  onStatusFilter,
  isLoading,
  error,
  onRefresh,
}) {
  const [waiveRow, setWaiveRow] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionInfo, setActionInfo] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [collectingDriverId, setCollectingDriverId] = useState(null);
  const columnsFit = useFitGridColumns(chargingEnabled);

  async function submitWaive() {
    if (!waiveRow) return;
    if (!waiveReason.trim()) {
      setActionError('A reason is required to waive an incidental.');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await api3Post(
        '/fleets/tolls-queue',
        { action: 'waive', incidentalId: waiveRow.id, reason: waiveReason.trim() },
        fleetsQuery(selectedFleetCode)
      );
      setWaiveRow(null);
      setWaiveReason('');
      onRefresh();
    } catch (e) {
      setActionError(e.message || 'Failed to waive incidental');
    }
    setActionLoading(false);
  }

  async function submitRetry(row) {
    if (!row?.id) return;
    if (!chargingEnabled) {
      setActionError('Retry is disabled while INCIDENTALS_CHARGING_ENABLED is off.');
      return;
    }
    setRetryingId(row.id);
    setActionError('');
    try {
      await api3Post(
        '/fleets/tolls-queue',
        { action: 'retry', incidentalId: row.id },
        fleetsQuery(selectedFleetCode)
      );
      onRefresh();
    } catch (e) {
      setActionError(e.message || 'Retry charge failed');
      onRefresh();
    }
    setRetryingId(null);
  }

  async function submitCollectDriver(driverId) {
    if (!driverId) return;
    if (!chargingEnabled) {
      setActionError('Collect is disabled while INCIDENTALS_CHARGING_ENABLED is off.');
      return;
    }
    setCollectingDriverId(driverId);
    setActionError('');
    setActionInfo('');
    try {
      const data = await api3Post(
        '/fleets/tolls-queue',
        { action: 'collect_driver', driverId },
        fleetsQuery(selectedFleetCode)
      );
      setActionInfo(data?.message || 'Collect finished');
      onRefresh();
    } catch (e) {
      setActionError(e.message || 'Collect failed');
      onRefresh();
    }
    setCollectingDriverId(null);
  }

  const columns = useMemo(
    () => [
      {
        headerName: 'Driver',
        field: 'driver_name',
        rowGroup: true,
        hide: true,
        filter: true,
      },
      {
        headerName: 'Reservation',
        field: 'parent_res_id',
        rowGroup: true,
        hide: true,
        filter: true,
      },
      { headerName: 'Created', field: 'created', width: 155, minWidth: 140 },
      { headerName: 'Status', field: 'status', width: 100, minWidth: 90, filter: true },
      { headerName: 'Fleet', field: 'fleet_code', width: 90, minWidth: 80, filter: true },
      { headerName: 'Category', field: 'fee_category', width: 95, minWidth: 85, filter: true },
      {
        headerName: 'Owed',
        field: 'owed_amount',
        width: 100,
        minWidth: 90,
        aggFunc: 'sum',
        valueFormatter: (p) => (p.value == null ? '' : money(p.value)),
      },
      {
        headerName: 'Collected',
        field: 'collected_amount',
        width: 120,
        minWidth: 100,
        aggFunc: 'sum',
        valueFormatter: (p) => (p.value == null ? '' : money(p.value)),
      },
      {
        headerName: 'Amount',
        field: 'amount',
        width: 90,
        minWidth: 80,
        valueFormatter: (p) =>
          p.value == null ? '' : money(p.value),
      },
      {
        headerName: 'Card',
        field: 'card_block',
        width: 170,
        minWidth: 140,
        filter: true,
      },
      { headerName: 'Res', field: 'res_id', width: 160, minWidth: 130, filter: true },
      { headerName: 'Car', field: 'car_name', width: 120, minWidth: 100, filter: true },
      { headerName: 'Local time', field: 'transaction_local_datetime', width: 170, minWidth: 155 },
      { headerName: 'Location', field: 'location', minWidth: 220, filter: true },
      {
        headerName: 'Attempts',
        field: 'charge_attempt_count',
        width: 95,
        minWidth: 85,
      },
      {
        headerName: 'Last error',
        field: 'last_charge_error',
        minWidth: 160,
        filter: true,
        tooltipField: 'last_charge_error',
      },
      { headerName: 'Paid at', field: 'paid_at', width: 170, minWidth: 155 },
      {
        headerName: 'Source',
        field: 'source_type',
        width: 110,
        minWidth: 100,
        cellRenderer: (p) => {
          const t = p.data?.source_type;
          const id = p.data?.source_id;
          if (t === 'toll' && id) return `toll …${String(id).slice(-6)}`;
          return t || '';
        },
      },
      {
        headerName: 'Actions',
        width: 200,
        minWidth: 200,
        pinned: 'right',
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const st = params.data?.status;
          if (st !== 'open' && st !== 'give_up') return null;
          const busy = retryingId === params.data?.id;
          return (
            <Flex gap="0.35rem">
              <Button
                size="small"
                variation="primary"
                isDisabled={!chargingEnabled || busy || actionLoading}
                onClick={() => submitRetry(params.data)}
              >
                {busy ? 'Retrying…' : 'Retry'}
              </Button>
              <Button
                size="small"
                variation="warning"
                isDisabled={busy || actionLoading}
                onClick={() => {
                  setWaiveRow(params.data);
                  setWaiveReason('');
                }}
              >
                Waive
              </Button>
            </Flex>
          );
        },
      },
    ],
    [chargingEnabled, retryingId, actionLoading]
  );

  return (
    <View>
      <Text>
        Charge outcomes for attributed incidentals, grouped by driver then parent reservation.
        Open still counts as owed until paid or waived
        {` — ${money(totalOwed)} owed (${Number(unpaidCount || 0)} unpaid), ${money(totalCollected)} collected`}.
      </Text>
      {!chargingEnabled ? (
        <Text marginTop="0.5rem">
          <i>
            Auto-charging is currently disabled (INCIDENTALS_CHARGING_ENABLED) — balances are tracked
            but not being collected.
          </i>
        </Text>
      ) : null}
      <Flex gap="0.5rem" marginTop="0.75rem" wrap="wrap">
        {COLLECTION_STATUS_FILTERS.map((f) => {
          const count =
            f.id === 'all'
              ? Object.values(statusCounts || {}).reduce((a, b) => a + Number(b || 0), 0)
              : statusCounts?.[f.id] || 0;
          const selected = statusFilter === f.id;
          return (
            <Button
              key={f.id}
              size="small"
              variation={selected ? 'primary' : undefined}
              onClick={() => onStatusFilter(f.id)}
            >
              {f.label} ({count})
            </Button>
          );
        })}
      </Flex>
      {error || actionError ? (
        <Alert variation="error" marginTop="1rem">
          {error || actionError}
        </Alert>
      ) : null}
      {actionInfo ? (
        <Alert variation="info" marginTop="1rem">
          {actionInfo}
        </Alert>
      ) : null}
      {isLoading || actionLoading ? <Loadingmini /> : null}
      <div
        ref={columnsFit.rootRef}
        className="ag-theme-quartz"
        style={{ height: 520, width: '100%', marginTop: '1rem' }}
      >
        <AgGridReact
          rowData={rows}
          columnDefs={columns}
          theme={themeQuartz}
          onGridReady={columnsFit.onGridReady}
          onFirstDataRendered={columnsFit.onFirstDataRendered}
          onRowDataUpdated={columnsFit.onRowDataUpdated}
          defaultColDef={{
            sortable: true,
            resizable: true,
          }}
          autoGroupColumnDef={{
            headerName: 'Driver / reservation',
            minWidth: 360,
            cellRendererParams: {
              innerRenderer: (params) => {
                const leaf = params.node?.allLeafChildren?.[0]?.data || params.data;
                const colId = params.node?.rowGroupColumn?.getColId?.() || params.node?.field;
                if (colId === 'driver_name') {
                  const id = leaf?.driver_id;
                  const name = params.value || leaf?.driver_name || (id != null ? `Driver ${id}` : '');
                  const unpaid = (params.node?.allLeafChildren || []).some((c) => {
                    const st = c.data?.status;
                    return st === 'open' || st === 'give_up';
                  });
                  const busy = collectingDriverId === id;
                  return (
                    <Flex alignItems="center" gap="0.5rem" wrap="nowrap">
                      {id ? <NavLink to={withFleets(`/user/${id}`, leaf?.fleet_code || selectedFleetCode)}>{name}</NavLink> : name}
                      {unpaid && chargingEnabled ? (
                        <Button
                          size="small"
                          variation="primary"
                          isDisabled={busy || actionLoading || collectingDriverId != null}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            submitCollectDriver(id);
                          }}
                        >
                          {busy ? 'Collecting…' : 'Collect unpaid'}
                        </Button>
                      ) : null}
                    </Flex>
                  );
                }
                if (colId === 'parent_res_id') {
                  const rid = params.value || leaf?.parent_res_id;
                  if (rid) return <NavLink to={withFleets(`/reservation/${rid}`, leaf?.fleet_code || selectedFleetCode)}>{rid}</NavLink>;
                  return rid || '';
                }
                return params.value;
              },
            },
          }}
          groupDisplayType="singleColumn"
          groupTotalRow="bottom"
          grandTotalRow="bottom"
          enableBrowserTooltips
          tooltipShowDelay={400}
        />
      </div>

      {waiveRow ? (
        <View
          marginTop="1.5rem"
          padding="1rem"
          backgroundColor="var(--amplify-colors-background-secondary)"
        >
          <Heading level={5}>
            Waive ${Number(waiveRow.amount || 0).toFixed(2)} {waiveRow.fee_category} (#{waiveRow.id})
          </Heading>
          <TextField
            label="Reason (required)"
            value={waiveReason}
            onChange={(e) => setWaiveReason(e.target.value)}
            marginBottom="0.75rem"
          />
          <Flex gap="0.5rem">
            <Button variation="warning" onClick={submitWaive}>
              Confirm waive
            </Button>
            <Button
              onClick={() => {
                setWaiveRow(null);
                setWaiveReason('');
              }}
            >
              Cancel
            </Button>
          </Flex>
        </View>
      ) : null}
    </View>
  );
}

export default function Incidentals() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedFleetCode, setSelectedFleetCode] = useState(null);
  const [tab, setTab] = useState('attribution');
  const [statusFilter, setStatusFilter] = useState('all');
  const [chargingEnabled, setChargingEnabled] = useState(true);
  const [attrRows, setAttrRows] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [attrOffset, setAttrOffset] = useState(0);
  const [collectionRows, setCollectionRows] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [collectionTotalOwed, setCollectionTotalOwed] = useState(0);
  const [collectionTotalCollected, setCollectionTotalCollected] = useState(0);
  const [collectionUnpaidCount, setCollectionUnpaidCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const fleetPageKey = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fleetsParam = params.get('fleets');
    if (fleetsParam) {
      setSelectedFleetCode(fleetsParam.split(',')[0] || null);
    } else {
      setSelectedFleetCode(null);
    }
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (tabParam === 'collection' || tabParam === 'attribution') {
      setTab(tabParam);
    }
    const statusParam = (params.get('status') || '').toLowerCase();
    if (COLLECTION_STATUS_FILTERS.some((f) => f.id === statusParam)) {
      setStatusFilter(statusParam);
    }
    const nextFleet = fleetsParam ? fleetsParam.split(',')[0] || '' : '';
    if (fleetPageKey.current !== null && fleetPageKey.current !== nextFleet) {
      setAttrOffset(0);
    }
    fleetPageKey.current = nextFleet;
  }, [location.search]);

  function updateTab(next) {
    setTab(next);
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    if (next !== 'collection') params.delete('status');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }

  function updateStatusFilter(next) {
    setStatusFilter(next);
    const params = new URLSearchParams(location.search);
    params.set('tab', 'collection');
    if (next && next !== 'all') params.set('status', next);
    else params.delete('status');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError('');
      try {
        const query = fleetsQuery(selectedFleetCode);
        if (tab === 'collection') {
          query.view = 'collection';
          if (statusFilter && statusFilter !== 'all') query.status = statusFilter;
        } else {
          query.view = 'attribution';
          query.offset = attrOffset;
          query.limit = ATTR_PAGE_SIZE;
        }
        const data = await api3Get('/fleets/tolls-queue', query);
        if (cancelled) return;
        setChargingEnabled(Boolean(data?.chargingEnabled));
        if (tab === 'collection') {
          setCollectionRows(data?.resp || []);
          setStatusCounts(data?.statusCounts || {});
          setCollectionTotalOwed(Number(data?.totalOwed || 0));
          setCollectionTotalCollected(Number(data?.totalCollected || 0));
          setCollectionUnpaidCount(Number(data?.unpaidCount || 0));
        } else {
          setAttrRows(data?.resp || []);
          setTotalPending(data?.totalPending || 0);
        }
        // Refresh the other tab's badge cheaply when on attribution (pending count known);
        // when on collection, also pull attribution count for the badge.
        if (tab === 'collection') {
          const attrData = await api3Get('/fleets/tolls-queue', {
            ...fleetsQuery(selectedFleetCode),
            view: 'attribution',
            countsOnly: true,
          });
          if (!cancelled) setTotalPending(attrData?.totalPending || 0);
        } else {
          const colData = await api3Get('/fleets/tolls-queue', {
            ...fleetsQuery(selectedFleetCode),
            view: 'collection',
            status: 'all',
          });
          if (!cancelled) setStatusCounts(colData?.statusCounts || {});
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load incidentals');
          if (tab === 'collection') setCollectionRows([]);
          else {
            setAttrRows([]);
            setTotalPending(0);
          }
          setCollectionTotalOwed(0);
          setCollectionTotalCollected(0);
          setCollectionUnpaidCount(0);
        }
      }
      if (!cancelled) setIsLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedFleetCode, tab, statusFilter, refreshKey, attrOffset]);

  const collectionBadge = useMemo(() => {
    const open = Number(statusCounts.open || 0);
    const giveUp = Number(statusCounts.give_up || 0);
    return open + giveUp;
  }, [statusCounts]);

  return (
    <View padding="1rem">
      <Heading level={3}>Incidentals</Heading>
      <Text>
        Attribute unmatched tolls, then track collection outcomes (open, paid, waived).
      </Text>
      {selectedFleetCode ? (
        <Text marginTop="0.5rem">Fleet filter: {selectedFleetCode}</Text>
      ) : (
        <Text marginTop="0.5rem">Showing all fleets in your access list.</Text>
      )}
      <TabBar
        active={tab}
        onChange={updateTab}
        badges={{ attribution: totalPending, collection: collectionBadge }}
      />
      {tab === 'collection' ? (
        <CollectionPanel
          selectedFleetCode={selectedFleetCode}
          chargingEnabled={chargingEnabled}
          rows={collectionRows}
          statusCounts={statusCounts}
          totalOwed={collectionTotalOwed}
          totalCollected={collectionTotalCollected}
          unpaidCount={collectionUnpaidCount}
          statusFilter={statusFilter}
          onStatusFilter={updateStatusFilter}
          isLoading={isLoading}
          error={error}
          onRefresh={() => setRefreshKey((k) => k + 1)}
        />
      ) : (
        <AttributionPanel
          selectedFleetCode={selectedFleetCode}
          chargingEnabled={chargingEnabled}
          rows={attrRows}
          totalPending={totalPending}
          pageOffset={attrOffset}
          pageSize={ATTR_PAGE_SIZE}
          onPage={setAttrOffset}
          isLoading={isLoading}
          error={error}
          onRefresh={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </View>
  );
}
